import React, {useEffect, useRef, useState} from 'react';
import {Col, DatePicker, Form, Input, message, Row} from 'antd';
import {ProForm, ProFormInstance} from '@ant-design/pro-form';
import {WdiCardTitle, WdiModalAppResult} from '@/components/WdiControls';
import {FormPageProps} from '@/components/WdiBus/FormPage';
import {ApplyFooter, ApplyFooterType} from '@/components/WdiBus/ApplyFooter';
import moment from 'moment';
import {FileUploadUtility, WdiFileUpload} from '@/components/WdiControls/WdiFileUpload';
import {Enum} from '@/enums/system';
import {ResponseStatus} from '@/utils/request';
import {initApplyPageContext, ViewEntityProps} from '../../components/data';
import DataStore from '@/components/GridWidget/DataStore';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import {FormSaDetailsForApplyPanel} from './Details';
import SAWarningInfoApp from '../../components/WarningInformation';
import {FormSa, FormSaDetail} from '@/services/sales/sa';
import {WarningInfoAppProps} from '@/components/WdiBus/PanelForWarning';
import {ProjectToolTipApp} from '../../components/ProjectCmpt';
import ProjectBudgetInfoPanel from '../../components/ProjectBudgetInfo';
import {BudgetProject} from '@/services/sales/bp/budgetProject';
import {WdiEditor} from "@/components/WdiControls/WdiEditor";
import {FormApi} from "@/services/sales";

const getPageResult = (values: any, viewEntity: ViewEntityProps, statusId: number, dataSource: any[]) => {
  const result: WdiModalAppResult = {code: ResponseStatus.UNKNOWN};

  const {activityDate, supplyDate, fileInfo, ...entity} = values;

  const req: { form: any; formSa: any; formSaDetails: any[] } = {
    form: {
      statusId: statusId,
      pageTypeId: viewEntity.pageTypeId,
      formTypeId: viewEntity.formTypeId,
      budgetProjectId: viewEntity?.budgetProject?.budgetProjectId,
      userId: viewEntity?.userId,
      positionId: viewEntity?.positionId,
    },
    formSa: {},
    formSaDetails: [],
  };

  if (viewEntity.formId) {
    //草稿打开
    req.form.formId = viewEntity.formId;
  } else if (viewEntity.relatedFormId) {
    if (
      viewEntity.relationType === Enum.FormRelationType.REJECTED ||
      viewEntity.relationType === Enum.FormRelationType.MODIFIED
    ) {
      // 退回待修改,修改单据
      req.form.relatedFormId = viewEntity.relatedFormId;
      req.form.relationType = viewEntity.relationType;
    }
    req.form.formId = 0;
  }

  if (activityDate && activityDate.length == 2) {
    entity.activityStartDate = moment(activityDate[0]).format('YYYY-MM-DD');
    entity.activityEndDate = moment(activityDate[1]).format('YYYY-MM-DD');
  } else {
    result.message = '无效的活动期间';
    return result;
  }
  if (supplyDate && supplyDate.length == 2) {
    entity.supplyStartDate = moment(supplyDate[0]).format('YYYY-MM-DD');
    entity.supplyEndDate = moment(supplyDate[1]).format('YYYY-MM-DD');
  } else {
    result.message = '无效的供货期间';
    return result;
  }

  const info = FileUploadUtility.serialize(fileInfo);
  entity.fileName = info?.names;
  entity.filePath = info?.urls;

  req.formSa = entity;
  if (dataSource && dataSource.length) {
    dataSource.forEach((item, itemIndex) => {
      item.rowNumber = itemIndex + 1;
    });
  } else {
    result.message = '请填写费用明细!';
    return result;
  }

  req.formSaDetails = dataSource;
  result.data = req;
  result.code = ResponseStatus.SUCCESS;
  return result;
};

const GeneralActivityApplyApp: React.FC<FormPageProps> = (props) => {
  const formRef = useRef<ProFormInstance>();

  const [entity, setEntity] = useState<ViewEntityProps>();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [budgetProjectDetails, setBudgetProjectDetails] = useState<any[]>([]);
  const [warningSettings, setWarningSettings] = useState<WarningInfoAppProps>({visible: false});
  const [formSaDate, setFormSaDate] = useState<{
    activityStartDate?: any, activityEndDate?: any,
    supplyStartDate?: any, supplyEndDate?: any
  }>();
  const [editorRef, setEditorRef] = useState<any>()

  const onDetailsChange = (data: any) => {
    setDataSource(data);
  };

  useEffect(() => {
    (async () => {
      const params = props.data;
      if (params) {
        const result = await initApplyPageContext(params);
        if (result.code !== ResponseStatus.SUCCESS) {
          message.error(result.message);
          return;
        }
        const viewEntity: ViewEntityProps = result.data;

        if (viewEntity.formSa) {
          formRef.current?.setFieldsValue({
            activityName: viewEntity.formSa?.activityName,
            activityDate: [
              moment(viewEntity.formSa?.activityStartDate),
              moment(viewEntity.formSa?.activityEndDate),
            ],
            supplyDate: [
              moment(viewEntity.formSa?.supplyStartDate),
              moment(viewEntity.formSa?.supplyEndDate),
            ],
            remark: viewEntity.formSa?.remark,
            fileInfo: FileUploadUtility.parse(
              viewEntity.formSa?.fileName,
              viewEntity.formSa?.filePath,
            ),
          });

          setFormSaDate({
            activityStartDate: viewEntity.formSa.activityStartDate,
            activityEndDate: viewEntity.formSa.activityEndDate,
            supplyStartDate: viewEntity.formSa.supplyStartDate,
            supplyEndDate: viewEntity.formSa.supplyEndDate,
          });
        }

        if (viewEntity.formSa?.formSaId) {
          const response = await FormSaDetail.getsEx(viewEntity.formSa?.formSaId)
          // 后补类，变更申请单
          let relationFormExported = false;
          if (viewEntity.relatedFormId) {
            const relatedFormResp = await FormApi.get(viewEntity.relatedFormId);
            if (relatedFormResp.data.exportStatus1 == Enum.ExportStatus.EXPORT_SUCCESS) {
              relationFormExported = true;
            }
          }
          const newDataSource: any[] = [];
          response.data?.forEach((item) => {
            if (viewEntity.relationType == Enum.FormRelationType.REJECTED || viewEntity.relationType == Enum.FormRelationType.MODIFIED) {
              newDataSource.push(Object.assign({}, item, {formSaDetailIdOriginal: relationFormExported ? item.formSaDetailId : item.formSaDetailIdOriginal}));
            } else {
              newDataSource.push(Object.assign({}, item, {formSaDetailIdOriginal: null}));
            }
          });
          setDataSource(DataStore.init(newDataSource));
        }
        if (viewEntity.budgetProject) {
          formRef.current?.setFieldsValue({
            project: viewEntity.budgetProject,
          });

          BudgetProject.getForDetails(viewEntity.budgetProject.budgetProjectId).then((response) => {
            setBudgetProjectDetails(DataStore.init(response.data));
          });
        }

        setEntity(viewEntity);
      }
    })();
  }, [props.data]);

  useEffect(() => {
    if (props.setPageTitle && entity) {
      let title = '活动申请';
      if (entity.formSa) {
        title += '【编辑】';
      }
      props.setPageTitle(title);
    }
  }, [props.setPageTitle, entity]);

  return (
    <>
      <SAWarningInfoApp {...warningSettings} />
      <ProForm
        formRef={formRef}
        submitter={{
          render: () => {
            return (
              <ApplyFooter
                viewEntity={entity}
                setLoading={props.setLoading}
                submitBackUrl="/sales/sa/myactivity"
                doSubmit={async (type: number, forceSubmit: boolean) => {
                  if (!entity || !dataSource) {
                    return;
                  }
                  const remark = await editorRef?.current?.getHtml?.();
                  const values = await formRef.current?.validateFieldsReturnFormatValue?.();
                  if (values && values.errorFields && values.errorFields.length) {
                    return;
                  }
                  const statusId = type === ApplyFooterType.Draft ? Enum.FormStatus.DRAFT : Enum.FormStatus.AWAITING;
                  values.remark = remark;
                  const req = getPageResult(values, entity, statusId, dataSource);

                  if (req.code !== ResponseStatus.SUCCESS) {
                    message.error(req.message);
                    return;
                  }

                  props?.setLoading?.(true);
                  if (type === ApplyFooterType.Validate) {
                    return await FormSa.Buttons.validate(req.data);
                  } else {
                    return await FormSa.Buttons.apply({
                      forceSubmit: forceSubmit,
                      ...req.data,
                    });
                  }
                }}
                del={async () => {
                  if (!entity || !entity.formId) {
                    return;
                  }
                  return await FormSa.Buttons.del(entity.formId);
                }}
                showWarning={(result: WdiModalAppResult, submitCallback: any) => {
                  setWarningSettings({
                    visible: true,
                    title: '提示信息',
                    wdiPanelList: result.data,
                    onClose: () => {
                      setWarningSettings({visible: false});
                    },
                    onSave: async () => {
                      return await submitCallback();
                    },
                  });
                }}
              />
            );
          },
        }}
      >
        <WdiPanel title={<WdiCardTitle title="活动基本信息"/>}>
          <Row gutter={[16, 24]} style={{rowGap: 0}}>
            <Col className="gutter-row" span={4}>
              <Form.Item
                name="activityName"
                label="活动名称"
                rules={[{required: true, message: '活动名称不能为空!'}]}
              >
                <Input/>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={8}>
              <Form.Item
                required
                name="activityDate"
                label="活动期间"
                rules={[{required: true, message: '活动期间不能为空!'}]}
              >
                <DatePicker.RangePicker
                  onChange={(values) => {
                    if (values && values.length === 2) {
                      formRef.current?.setFieldsValue({supplyDate: values});
                      setFormSaDate({
                        activityStartDate: moment(values[0]).format('YYYY-MM-DD'),
                        activityEndDate: moment(values[1]).format('YYYY-MM-DD'),
                        supplyStartDate: moment(values[0]).format('YYYY-MM-DD'),
                        supplyEndDate: moment(values[1]).format('YYYY-MM-DD'),
                      });
                    } else {
                      setFormSaDate({});
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={8}>
              <Form.Item
                required
                name="supplyDate"
                label="供货期间"
                rules={[{required: true, message: '供货期间不能为空!'}]}
              >
                <DatePicker.RangePicker
                  onChange={(values) => {
                    if (values && values.length === 2) {
                      setFormSaDate(Object.assign({}, formSaDate, {
                        supplyStartDate: moment(values[0]).format('YYYY-MM-DD'),
                        supplyEndDate: moment(values[1]).format('YYYY-MM-DD'),
                      }));
                    } else {
                      setFormSaDate(Object.assign({}, formSaDate, {supplyStartDate: '', supplyEndDate: ''}));
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="budgetProjectId" label="budgetProjectId" hidden>
                <Input/>
              </Form.Item>
              <Form.Item name="project" label="专项项目">
                <ProjectToolTipApp/>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={16}>
              <Form.Item name="remark" label="方案简述" rules={[{required: true, message: '方案简述不能为空!'}]}>
                <WdiEditor setRef={setEditorRef}/>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={8}>
              <Form.Item
                name="fileInfo"
                label="附件"
                valuePropName="fileList"
                getValueFromEvent={(e: any) => {
                  return e.fileList || [];
                }}
              >
                <WdiFileUpload leftTopInfo="请选择文件并上传(单个文件不能超500M)"/>
              </Form.Item>
            </Col>
          </Row>
        </WdiPanel>
        <ProjectBudgetInfoPanel dataSource={budgetProjectDetails}/>
        <FormSaDetailsForApplyPanel
          data={{
            form: Object.assign({}, entity?.form, {
              relatedFormId: entity?.relatedFormId,
              relationType: entity?.relationType,
              pageTypeId: entity?.pageTypeId,
              formTypeId: entity?.formTypeId,
            }),
            formSa: {
              pageTypeId: entity?.pageTypeId,
              formTypeId: entity?.formTypeId,
              budgetProjectId: entity?.budgetProjectId,
              ...formSaDate,
            },
            ecFieldConfigs: entity?.ecFieldConfigs,
          }}
          dataSource={dataSource}
          onChange={onDetailsChange}
        />
      </ProForm>
    </>
  );
};
export default GeneralActivityApplyApp;
